import { Anchor, Text } from 'grommet';
import { getSortedFormFields } from '@utils/forms';
import type { ModalInfo } from '@customTypes/types';
import { helpWithOptions, primaryBlockStorageOptions, privateCloudBusinessOptions } from './DSCCFormOptions';
import { MODAL_INFO_DATA } from '@constants/modalInfo';

export const getDSCCFormData = (target, helpWith, helpWithSecondary, companyName, severity, issueTitle, product?) => {
  let namespace: string;
  let subject: string;

  switch (helpWith) {
    case helpWithOptions.disasterRecovery:
      namespace = 'hpe_dssupport_zetro';
      subject = `${helpWith}, ${severity}, ${helpWithSecondary}, ${companyName}`;
      break;
    case helpWithOptions.cloudPhysics:
      namespace = 'hpe_dssupport_cloud';
      subject = `${helpWith}, ${helpWithSecondary}, ${companyName}`;
      break;
    case helpWithOptions.fileStorage:
    case helpWithOptions.backupAndRecovery:
    case helpWithOptions.dataObservability:
      namespace = 'hpe_dssupport_alletra6000';
      subject = `Support request (form): ${helpWith}, ${helpWithSecondary}, ${companyName}`;
      break;
    case helpWithOptions.storageFabric:
      namespace = 'hpe_dssupport_alletra9000';
      subject = `${helpWith}, ${helpWithSecondary}, {formId}`;
      break;
    default:
      namespace = 'hpe_dssupport_alletra6000';
      subject = `${helpWith}, ${helpWithSecondary}, ${companyName}`;
  }

  if (helpWith === helpWithOptions.blockStorage) {
    switch (helpWithSecondary) {
      case primaryBlockStorageOptions.dashboard:
      case primaryBlockStorageOptions.alletra6000:
      case primaryBlockStorageOptions.alletra5000:
      case primaryBlockStorageOptions.nimbleStorage:
        namespace = 'hpe_dssupport_alletra6000';
        subject = `Support request (form): ${helpWith}, ${helpWithSecondary}, ${companyName}, ${issueTitle}`;
        break;
      case primaryBlockStorageOptions.alletra9000:
      case primaryBlockStorageOptions.primeraStorage:
        namespace = 'hpe_dssupport_alletra9000';
        subject = `${helpWith}, ${helpWithSecondary}, ${issueTitle}, {formId}`;
        break;
      case primaryBlockStorageOptions.alletraStorage:
      case primaryBlockStorageOptions.blockStorageAWS:
        namespace = 'hpe_storage_alletra_mp_all';
        subject = `Support request (form): ${helpWith}, ${helpWithSecondary}, ${companyName}, ${issueTitle}`;
        break;
    }
  }

  if (helpWith === helpWithOptions.privateCloudAI) {
    namespace = 'hpe_pcai';
    subject = `${helpWith} {formId}`;
  }

  if (helpWith === helpWithOptions.privateCloudBusiness) {
    switch (helpWithSecondary) {
      case privateCloudBusinessOptions.dhci:
        namespace = 'hpe_dssupport_alletra6000';
        subject = `Support request (form): ${helpWith}, ${helpWithSecondary}, ${companyName}`;
        break;
      case privateCloudBusinessOptions.alletraStorageBlock:
        namespace = 'hpe_storage_alletra_mp_all';
        subject = `Support request (form): ${helpWith}, ${helpWithSecondary}, ${companyName}`;
        break;
      case privateCloudBusinessOptions.simpliVity:
        namespace = 'hpe_pcbe_simplivity';
        subject = `${helpWith}, ${helpWithSecondary}, {formId}`;
        break;
    }
  }

  return getSortedFormFields(target, namespace, subject, '', product);
};

const getExtraData = (bodyText: string, link: string) => {
  return {
    extraData: <>
      {bodyText}<br/>
      <Text>Not in USA? </Text>
      <Anchor weight="normal"
              href={link}
              label="View our international phone number list."></Anchor>
    </>
  }
};

export const getDSCCModalData = (helpWith: string, helpWithSecondary: string): ModalInfo => {
  let bodyText;
  let link;
  let isExtraData = true;

  switch (helpWith) {
    case helpWithOptions.disasterRecovery:
      bodyText = 'For urgent issues, please call +1-866-271-3145.';
      link = 'https://www.zerto.com/support-and-services/support/';
      break;
    case helpWithOptions.cloudPhysics:
      // No Phone Support
      isExtraData = false;
      break;
    case helpWithOptions.fileStorage:
      bodyText = 'For urgent issues, please call 1-800-633-3600 and voice option "Alletra Storage MP File".'
      link = 'https://www.hpe.com/psnow/doc/a00039121enw.pdf';
      break;
    case helpWithOptions.backupAndRecovery:
    case helpWithOptions.dataObservability:
      // Storage Support
      bodyText = 'For urgent issues, please call 1-800-986-6809.';
      link = 'https://www.hpe.com/psnow/doc/a50004235ENW';
      break;
    case helpWithOptions.objectService:
      bodyText = 'For urgent issues, please call 1-800-633-3600 and voice option "Alletra MP X10000".';
      link = 'https://www.hpe.com/psnow/doc/a00039121enw.pdf';
      break;
    case helpWithOptions.storageFabric:
      bodyText = 'For urgent issues, please call 1-800-633-3600 and voice option "Mission Critical Block Storage".';
      link = 'https://www.hpe.com/psnow/doc/a00039121enw.pdf';
      break;
    default:
      bodyText = 'For urgent issues, please call 1-800-986-6809.';
      link = 'https://www.hpe.com/psnow/doc/a50004235ENW';
  }

  if (helpWith === helpWithOptions.blockStorage) {
    switch (helpWithSecondary) {
      case primaryBlockStorageOptions.dashboard:
      case primaryBlockStorageOptions.alletra5000:
      case primaryBlockStorageOptions.alletra6000:
      case primaryBlockStorageOptions.nimbleStorage:
        bodyText = 'For urgent issues, please call 1-800-986-6809.';
        link = 'https://www.hpe.com/psnow/doc/a50004235ENW';
        break;
      case primaryBlockStorageOptions.alletra9000:
      case primaryBlockStorageOptions.primeraStorage:
        bodyText = 'For urgent issues, please call 1-800-633-3600 and voice option "Alletra 9000".';
        link = 'https://www.hpe.com/psnow/doc/a00039121enw.pdf';
        break;
      case primaryBlockStorageOptions.alletraStorage:
      case primaryBlockStorageOptions.blockStorageAWS:
        bodyText = 'For urgent issues, please call 1-800-633-3600 and voice option "Alletra Storage MP".'
        link = 'https://www.hpe.com/psnow/doc/a00039121enw.pdf';
        break;
    }
  }

  if (helpWith === helpWithOptions.privateCloudBusiness) {
    switch (helpWithSecondary) {
      case privateCloudBusinessOptions.dhci:
        bodyText = 'For urgent issues, please call 1-800-986-6809.';
        link = 'https://www.hpe.com/psnow/doc/a50004235ENW';
        break;
      case privateCloudBusinessOptions.alletraStorageBlock:
        bodyText = 'For urgent issues, please call 1-800-633-3600 and voice option “Alletra Storage MP”.';
        link = 'https://www.hpe.com/psnow/doc/a50004235ENW';
        break;
      case privateCloudBusinessOptions.simpliVity:
        bodyText = 'For urgent issues, please call 1-800-633-3600.';
        link = 'https://www.hpe.com/psnow/doc/a00039121enw.pdf';
        break;
    }
  }

  return {
    ...MODAL_INFO_DATA.success,
    statusText: 'Your support request has been sent',
    statusMessage: 'You will receive a confirmation at the email address you provided.',
    ...(isExtraData && getExtraData(bodyText, link)),
  };
};
